import React from 'react';
import { useLocation, Link } from 'react-router-dom';
import { Phone } from 'lucide-react';
import { track } from '../lib/analytics';

/**
 * StickyMobileCTA — fixed bottom bar shown on mobile/tablet (<lg).
 * Two equal-prominence buttons: Book Now (gold) + Call (outline).
 * Hidden on /admin/* routes.
 *
 * Desktop (>=lg): no floating element is rendered. The top nav already
 * surfaces "BOOK YOUR RIDE" and the hero embeds a quote widget, so a
 * floating call button only added visual clutter (and collided with the
 * Emergent platform badge in the bottom-right corner).
 */
export const StickyMobileCTA = () => {
  const { pathname } = useLocation();
  const isAdmin = pathname.startsWith('/admin');
  const onBookingPage = pathname === '/contact' || pathname === '/chauffeur/book';

  if (isAdmin) return null;

  return (
    <div
      data-testid="sticky-mobile-cta"
      className="lg:hidden"
      style={{
        position: 'fixed',
        // Sit just above the Emergent platform badge which lives bottom-right.
        bottom: 'calc(56px + env(safe-area-inset-bottom))',
        left: 0,
        right: 0,
        zIndex: 40,
        backgroundColor: 'rgba(8,8,8,0.95)',
        backdropFilter: 'blur(14px)',
        WebkitBackdropFilter: 'blur(14px)',
        borderTop: '1px solid rgba(194,168,120,0.25)',
        borderBottom: '1px solid rgba(194,168,120,0.12)',
        padding: '12px 14px',
        display: 'flex',
        gap: '10px',
        boxShadow: '0 -8px 32px rgba(0,0,0,0.4)',
      }}
    >
      {!onBookingPage && (
        <Link
          to="/contact"
          data-testid="sticky-mobile-book-cta"
          onClick={() => track('click_book_cta', { location: 'sticky_mobile_bar' })}
          style={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '14px',
            backgroundColor: '#C2A878',
            color: '#0A0A0A',
            fontSize: '13px',
            fontWeight: 600,
            letterSpacing: '1.5px',
            textTransform: 'uppercase',
            textDecoration: 'none',
            minHeight: '52px',
          }}
        >
          Book Now
        </Link>
      )}
      <a
        href="tel:+14379963801"
        data-testid="sticky-mobile-call-cta"
        aria-label="Call Toronto Royale"
        onClick={() => track('click_call_now', { location: 'sticky_mobile_bar', phone: '+1 (437) 996-3801' })}
        style={{
          flex: onBookingPage ? 1 : '0 0 52px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '10px',
          padding: '14px',
          border: '1px solid rgba(194,168,120,0.5)',
          color: '#C2A878',
          backgroundColor: 'transparent',
          fontSize: '13px',
          fontWeight: 500,
          letterSpacing: '1.5px',
          textTransform: 'uppercase',
          textDecoration: 'none',
          minHeight: '52px',
        }}
      >
        <Phone size={18} />
        {onBookingPage && <span>Call Now</span>}
      </a>
    </div>
  );
};
